import React from 'react';
import { AnyObject } from '@store/types';
import isEmpty from 'lodash/isEmpty';
import flattenDeep from 'lodash/flattenDeep';

import { DatePicker } from '@components/datePickers';
import { AsyncMultiSelect, AsyncSelect, MultiSelect, OptionType, Select } from '@components/Select';
import { AsyncUserMultiSelect, AsyncUserSelect } from '@components/ModelSelect/AsyncUserSelect';
import { LegalEntityAsyncMultiSelect, LegalEntityAsyncSelect } from '@components/ModelSelect/LegalEntrySelect';
import { parseAntdOptionTypeToOptionType } from '@utils/makeOptionTypes';
import { Input, MoneyInput } from '@components/Input';
import { Checkbox } from '@components/Checkbox';
import { useHandlers } from '@components/Table/CreationMenu/CreationMenuField/hooks/useHandlers';
import { FieldTitle, FieldTitleTheme } from '@components/FieldTitle';
import { SelectModifiers } from '@components/Select/Select';
import {
    BasePriceEditor,
    CompositePriceEditor,
    Duration,
    HiddenField,
    TranslationEditor,
} from '@modules/common/CreationMenuFields';
import { Status } from '@common/CreationMenuFields/Status/Status';
import { Text } from '@components/Text';
import { OrganizationAssignment } from '@common/CreationMenuFields/OrganizationAssignment';
import { translate } from '@chaika/i18n/helper';
import { TextModifiers } from '@components/Text/Text';
import { EhrInsuranceInfo } from '@common/CreationMenuFields/EhrInsuranceInfo';
import { isDefined } from '@utils/isDefined';
import { assertIsDefined } from '@utils/assertIsDefined';
import { DurationType } from '@common/CreationMenuFields/Duration/Duration';
import { Textarea } from '@components/Textarea';
import { TextAreaType } from '@components/Textarea';
import { DiscountEditor } from '@common/CreationMenuFields/DiscountEditor';
import { ActionTypes, DefaultFunctionResult, TableField, TableFieldType } from '@components/Table/CreationMenu';

const LOAD_OPTIONS_MESSAGE = 'Search select field is configured with loadOptions';

interface Props {
    menuData: object;
    field: TableField;
    fields: TableField[];
    onChange(params: object): Promise<void>;
}

interface Data {
    getField(fieldParams: TableField): React.JSX.Element;
}

export function useFieldCreator({ field, fields, onChange, menuData }: Props): Data {
    const { value, unValidText, options, resetField, onChangeSwitcher, onBlurInput } = useHandlers({
        onChange,
        field,
    });

    const isValid = validateValue(value);

    function getField(fieldParams: TableField): React.JSX.Element {
        let fieldType;

        const isHiddenType = !!fieldParams.depByFields?.actionType.includes(ActionTypes.Hidden);
        const isDisabledType = !!fieldParams.depByFields?.actionType.includes(ActionTypes.Disabled);
        const isOptionsByField = !!fieldParams.depByFields?.actionType.includes(ActionTypes.OptionsByField);
        const isValueCalculated = !!fieldParams.depByFields?.actionType.includes(ActionTypes.CalculateValueByField);

        const disabled = fieldParams.disabled || (isDisabledType && checkDependencies(fieldParams));
        const isHidden = isHiddenType && checkDependencies(fieldParams);

        if (isHidden) {
            fieldType = (
                <HiddenField
                    dataTest={fieldParams.dataTest}
                    resetField={resetField}
                />
            );
        } else {
            switch (fieldParams.type) {
                case TableFieldType.AsyncSelect: {
                    fieldType = (
                        <AsyncSelect
                            hideSelectedOptions
                            isValid={isValid}
                            notValidText={unValidText}
                            isClearable={fieldParams.isClearable}
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            selectedValue={value}
                            defaultOptions={parseAntdOptionTypeToOptionType(options ?? [])}
                            onChange={onChangeSwitcher}
                            loadOptions={async (value: string) => {
                                assertIsDefined(fieldParams.loadOptions, LOAD_OPTIONS_MESSAGE);
                                return parseAntdOptionTypeToOptionType(await fieldParams.loadOptions(value));
                            }}
                        />
                    );
                    break;
                }
                case TableFieldType.FieldTitle: {
                    fieldType = (
                        <FieldTitle
                            theme={FieldTitleTheme.WithFlex}
                            title={fieldParams.label}
                        />
                    );
                    break;
                }
                case TableFieldType.Text: {
                    // Read-only field — bind to the live prop so async-resolved values
                    // (e.g. byIds-hydrated legal entity names in view mode) display once they arrive.
                    fieldType = (
                        <Text
                            modifiers={(fieldParams.modifiers ?? []) as TextModifiers[]}
                            title={fieldParams.label}
                            text={fieldParams.value}
                        />
                    );
                    break;
                }
                case TableFieldType.Select: {
                    const modifiers = (fieldParams.modifiers ?? []) as SelectModifiers[];
                    const options = getOptions(isOptionsByField, fieldParams);

                    fieldType = (
                        <Select
                            isClearable
                            isValid={isValid}
                            notValidText={unValidText}
                            modifiers={modifiers}
                            isSearchable
                            resetValueOnUpdateOption={fieldParams.resetValueOnUpdateOption}
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            selectedValue={value}
                            options={options}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.Currency: {
                    const options = getOptions(isOptionsByField, fieldParams);
                    const valueData = isValueCalculated
                        ? (fieldParams.depByFields?.dependencies
                              ?.find(item => isDefined(item?.calculateValue))
                              ?.calculateValue?.(menuData) ?? value)
                        : value;

                    fieldType = (
                        <Select
                            isValid={isValid}
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            selectedValue={valueData}
                            options={fieldParams.options ? options : []}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.AsyncMultiSelect: {
                    fieldType = (
                        <AsyncMultiSelect
                            isValid={isValid}
                            notValidText={unValidText}
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            selectedValues={value ?? []}
                            defaultOptions={parseAntdOptionTypeToOptionType(options ?? [])}
                            onChange={onChangeSwitcher}
                            loadOptions={async (value: string) => {
                                assertIsDefined(fieldParams.loadOptions, LOAD_OPTIONS_MESSAGE);
                                return parseAntdOptionTypeToOptionType(await fieldParams.loadOptions(value));
                            }}
                        />
                    );
                    break;
                }
                case TableFieldType.SearchMultiSelect: {
                    fieldType = (
                        <MultiSelect
                            isValid={isValid}
                            isSearchable
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            selectedValues={value}
                            options={fieldParams.options ? parseAntdOptionTypeToOptionType(fieldParams.options) : []}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.AsyncUserSelect: {
                    fieldType = (
                        <AsyncUserSelect
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            defaultValue={value}
                            withFullName={fieldParams.withFullName}
                            withInitials={fieldParams.withInitials}
                            roleNames={fieldParams.roleNames}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.AsyncUserMultiSelect: {
                    fieldType = (
                        <AsyncUserMultiSelect
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            defaultValue={value ?? []}
                            withFullName={fieldParams.withFullName}
                            withInitials={fieldParams.withInitials}
                            roleNames={fieldParams.roleNames}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.AsyncLegalEntitySelect: {
                    fieldType = (
                        <LegalEntityAsyncSelect
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            isClearable={fieldParams.isClearable}
                            title={fieldParams.label}
                            value={value}
                            {...fieldParams.legalEntityScope}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.AsyncLegalEntityMultiSelect: {
                    fieldType = (
                        <LegalEntityAsyncMultiSelect
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            title={fieldParams.label}
                            value={value ?? []}
                            {...fieldParams.legalEntityScope}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.Money: {
                    const valueData = isValueCalculated
                        ? fieldParams.depByFields?.dependencies
                              ?.find(item => isDefined(item?.calculateValue))
                              ?.calculateValue?.(menuData)
                        : value;

                    fieldType = (
                        <MoneyInput
                            isValid={isValid}
                            withoutCurrency
                            title={fieldParams.label}
                            value={valueData}
                            disabled={disabled}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.TextArea: {
                    fieldType = (
                        <Textarea
                            isValid={isValid}
                            showValidStatus={false}
                            type={TextAreaType.NewTextAreaAutosize}
                            title={fieldParams.label}
                            value={value}
                            disabled={disabled}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.Input: {
                    fieldType = (
                        <Input
                            isValid={isValid}
                            title={fieldParams.label}
                            type={fieldParams.valueType ?? 'text'}
                            value={value}
                            disabled={disabled}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                            onBlur={fieldParams.valueType === 'tel' ? onBlurInput : undefined}
                        />
                    );
                    break;
                }
                case TableFieldType.Checkbox: {
                    fieldType = (
                        <Checkbox
                            label={fieldParams.label}
                            isChecked={value ?? !!value}
                            dataTest={fieldParams.dataTest}
                            disabled={disabled}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.DateField: {
                    fieldType = (
                        <DatePicker
                            isValid={isValid}
                            disabled={disabled}
                            isClearable={fieldParams.isClearable}
                            minDate={fieldParams.minDate}
                            dataTest={fieldParams.dataTest}
                            title={fieldParams.label}
                            name={fieldParams.label}
                            placeholder={translate('ddmmyyyy')}
                            selected={!isEmpty(value) ? new Date(value) : null}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.Duration: {
                    fieldType = (
                        <Duration
                            title={fieldParams.label}
                            name={fieldParams.label}
                            valueType={
                                isDefined(fieldParams?.valueType) ? (fieldParams?.valueType as DurationType) : undefined
                            }
                            dataTest={fieldParams.dataTest}
                            value={value}
                            placeholder={fieldParams.placeholder}
                            forcePlaceholder={!!fieldParams.placeholder}
                            disabled={disabled}
                            type={'number'}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.TranslationEditor: {
                    fieldType = (
                        <TranslationEditor
                            value={value}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.BasePriceEditor: {
                    fieldType = (
                        <BasePriceEditor
                            disabled={disabled}
                            value={value}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.CompositePriceEditor: {
                    const data = { legalEntityIds: [], ...menuData };
                    fieldType = (
                        <CompositePriceEditor
                            title={fieldParams.label}
                            disabled={disabled}
                            dataTest={fieldParams.dataTest}
                            legalEntityIds={data.legalEntityIds}
                            includedServices={value}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.Status: {
                    fieldType = (
                        <Status
                            value={value}
                            dataTest={fieldParams.dataTest}
                        />
                    );
                    break;
                }
                case TableFieldType.OrganizationAssignment: {
                    fieldType = (
                        <OrganizationAssignment
                            title={fieldParams.label}
                            value={value}
                            dataTest={fieldParams.dataTest}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.DiscountEditor: {
                    fieldType = (
                        <DiscountEditor
                            value={value}
                            onChange={onChangeSwitcher}
                        />
                    );
                    break;
                }
                case TableFieldType.EhrInsuranceInfo: {
                    fieldType = (
                        <EhrInsuranceInfo
                            value={value}
                            dataTest={fieldParams.dataTest}
                        />
                    );
                    break;
                }
                default:
                    // @ts-ignore
                    // eslint-disable-next-line
                    const neverType: never = fieldParams.type;
                    fieldType = <div>Illegal Field Type</div>;
            }
        }

        return fieldType;
    }

    function validateValue(value: any): boolean {
        let isValid = true;

        if (field.validationParams?.validationFunction) {
            isValid = field.validationParams.validationFunction(value);
        }

        return isValid;
    }

    function checkDependencies(fieldParams: TableField): boolean {
        let isNotValid = false;

        if (fieldParams.depByFields) {
            const { dependencies } = fieldParams.depByFields;

            const flattenFields = flattenDeep(fields);
            const depFields = flattenFields.filter(item => dependencies.find(dep => dep.field === item.fieldName));

            if (depFields.length > 0) {
                if (dependencies.some(dependency => !!dependency.checkFunction)) {
                    dependencies.forEach(dependency => {
                        if (!!dependency.checkFunction) {
                            dependency.checkFunction(depFields);
                        } else {
                            isNotValid = defaultCheckFunction(depFields, dependency.defaultFunctionResult);
                        }
                    });
                } else {
                    isNotValid = dependencies.some(dependency =>
                        defaultCheckFunction(depFields, dependency.defaultFunctionResult),
                    );
                }
            }
        }

        return isNotValid;
    }

    function defaultCheckFunction(
        dependencyFields: TableField[],
        functionResult: DefaultFunctionResult = DefaultFunctionResult.Positive,
    ): boolean {
        const isPositiveResult = functionResult === DefaultFunctionResult.Positive;
        return dependencyFields.some(field =>
            isPositiveResult ? !(menuData as AnyObject)[field.fieldName] : !!(menuData as AnyObject)[field.fieldName],
        );
    }

    function getOptions(isOptionsByField: boolean, fieldParams: TableField): OptionType[] {
        const dependency = isOptionsByField
            ? fieldParams.depByFields?.dependencies.find(item => item.getOptions)
            : undefined;

        const options =
            isOptionsByField && dependency?.getOptions
                ? dependency.getOptions(menuData)
                : ((fieldParams?.getOptions && fieldParams.getOptions()) ?? fieldParams.options ?? []);

        return parseAntdOptionTypeToOptionType(options);
    }

    return {
        getField,
    };
}
